import React, { useState, useEffect } from "react";
import { Form, Input } from "semantic-ui-react";
import { Box, Button, CircularProgress, Typography, Alert } from "@mui/material";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import PageTitle from "../../components/PageTitle/PageTitle";
import "./ResetPassword.css";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ROUTES } from "../../constants";
import { useTranslation } from "react-i18next";
import { getResetPasswordTokenInfo, resetPassword } from "../../services/resetPasswordService";
import { AuthPageStyles } from "../../utilities/themeColors";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import CompanyLogo from "../../components/CompanyLogo/CompanyLogo";

const ResetPassword = () => {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [combinedErrorMessage, setCombinedErrorMessage] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [confirmPasswordError, setConfirmPasswordError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [redirectCounter, setRedirectCounter] = useState(3); // countdown in seconds
	const [tokenInfoLoading, setTokenInfoLoading] = useState(false);
	const [resetEmail, setResetEmail] = useState("");
	const [validation, setValidation] = useState({
		minLength: false,
		uppercase: false,
		specialChar: false,
		number: false,
		confirmPass: false,
	});
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { authBackdropStyle } = useBrandingAssets();
	const { token: routeToken } = useParams();
	const location = useLocation();
	const queryToken = React.useMemo(() => {
		try {
			const params = new URLSearchParams(location.search);
			return params.get("token");
		} catch (_) {
			return null;
		}
	}, [location.search]);
	const activeToken = routeToken || queryToken;

	useEffect(() => {
		if (!activeToken) {
			setCombinedErrorMessage(t('ALERT_MESSAGES.INVALID_OR_MISSING_TOKEN'));
		}
	}, [activeToken, t]);

	useEffect(() => {
		let cancelled = false;

		const loadTokenInfo = async () => {
			if (!activeToken) return;
			setTokenInfoLoading(true);
			setResetEmail("");
			try {
				const response = await getResetPasswordTokenInfo(activeToken);
				if (cancelled) return;
				if (response?.status && response?.data?.user_email) {
					setResetEmail(response.data.user_email);
					return;
				}
				setCombinedErrorMessage(response?.message || t("ALERT_MESSAGES.INVALID_OR_MISSING_TOKEN"));
			} catch (_) {
				if (cancelled) return;
				setCombinedErrorMessage(t("ALERT_MESSAGES.INVALID_OR_MISSING_TOKEN"));
			} finally {
				if (!cancelled) setTokenInfoLoading(false);
			}
		};

		loadTokenInfo();
		return () => {
			cancelled = true;
		};
	}, [activeToken, t]);

	const handleResetPassword = async (e) => {
		e.preventDefault();
		setLoading(true);
		setPasswordError("");
		setConfirmPasswordError("");
		setCombinedErrorMessage("");

		// Validate password match
		if (password !== confirmPassword) {
			setCombinedErrorMessage(t('ALERT_MESSAGES.PASSWORDS_DO_NOT_MATCH'));
			setLoading(false);
			return;
		}

		if (!activeToken) {
			setCombinedErrorMessage(t('ALERT_MESSAGES.INVALID_OR_MISSING_TOKEN'));
			setLoading(false);
			return;
		}

		// Prepare request body
		const req_body = {
			token: activeToken,
			password,
		};

		try {
			// Make API call to reset password
			const response = await resetPassword(req_body);

			if (response.status) {
				setConfirmPassword("");
				setPassword("");
				setSuccessMessage(t('ALERT_MESSAGES.PASSWORD_RESET_SUCCESS'));
				setRedirectCounter(3); // start from 3 seconds

				// Countdown before redirect
				const interval = setInterval(() => {
					setRedirectCounter((prev) => {
						if (prev <= 1) {
							clearInterval(interval);
							navigate(ROUTES.LOGIN);
							return 0;
						}
						return prev - 1;
					});
				}, 1000);
			} else {
				setCombinedErrorMessage(t('ALERT_MESSAGES.RESET_PASSWORD_FAILED'));
			}
		} catch (error) {
			setCombinedErrorMessage(t('ALERT_MESSAGES.RESET_PASSWORD_GENERIC_ERROR'));
		}

		setLoading(false);
	};

	const validatePassword = (password) => {
		const minLength = password.length >= 8;
		const uppercase = /[A-Z]/.test(password);
		const specialChar = /[!@#$%^&*]/.test(password);
		const number = /[0-9]/.test(password);

		setValidation({
			minLength,
			uppercase,
			specialChar,
			number,
		});
	};

	const validateConfirmPassword = (confirmPasswd) => {
		const confirmpass = password === confirmPasswd;
		setValidation({
			...validation,
			confirmpass,
		});
	};

	const handlePasswordChange = (e) => {
		const newPassword = e.target.value;
		setPassword(newPassword);
		validatePassword(newPassword);
	};

	const handleConfirmPasswordChange = (e) => {
		setConfirmPassword(e.target.value);
		validateConfirmPassword(e.target.value);
	};

	const isFormValid = () => {
		return (
			validation.minLength &&
			validation.uppercase &&
			validation.specialChar &&
			validation.number &&
			password === confirmPassword
		);
	};

	return (
		<Box className="login-container" sx={authBackdropStyle || AuthPageStyles}>
			<Box className="login-form-container">
				<CompanyLogo alt="Logo" className="login-logo" />
				<div className="form-container">
					<PageTitle title={t('TITLES.RESET_PASSWORD')} />

					{/* Display the error alert on top of the form */}
					{combinedErrorMessage && (
						<Alert severity="error" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{combinedErrorMessage}
						</Alert>
					)}

					{successMessage && (
						<>
							<Alert severity="success" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
								{successMessage}
							</Alert>
							<Typography
								variant="body2"
								sx={{ marginTop: 1, color: "green", textAlign: "center" }}
							>
								Redirecting to login in {redirectCounter}...
							</Typography>
						</>
					)}

					{tokenInfoLoading && (
						<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
							<CircularProgress size={20} sx={{ color: "green" }} />
							<Typography variant="body2" sx={{ marginLeft: 1, color: "green" }}>
								{t("UI_TEXT.VERIFYING")}
							</Typography>
						</Box>
					)}

					{resetEmail && (
						<Alert severity="info" sx={{ width: "100%", marginBottom: "0", marginTop: "15px" }}>
							{`${t("LABELS.EMAIL") || "Email"}: ${resetEmail}`}
						</Alert>
					)}


					<Form className="login-form" onSubmit={handleResetPassword} error={!!combinedErrorMessage}>
						<Form.Field error={!!passwordError}>
							<Input
								fluid
								placeholder={t('LABELS.NEW_PASSWORD')}
								type="password"
								className="login-input"
								value={password}
								onChange={handlePasswordChange}
							/>
						</Form.Field>

						<Form.Field error={!!confirmPasswordError}>
							<Input
								fluid
								placeholder={t('LABELS.CONFIRM_NEW_PASSWORD')}
								type="password"
								className="login-input"
								value={confirmPassword}
								onChange={handleConfirmPasswordChange}
							/>
						</Form.Field>

						<div className="password-validation" style={{ marginBottom: "15px" }}>
							<p
								style={{
									fontSize: "11px",
									color: validation.minLength ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.minLength ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								{t('UI_TEXT.MINIMUM_EIGHT_CHARACTERS')}
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.uppercase ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.uppercase ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								{t('UI_TEXT.AT_LEAST_ONE_UPPERCASE')}
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.specialChar ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.specialChar ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								{t('UI_TEXT.AT_LEAST_ONE_SPECIAL_CHARACTERS')}
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.number ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.number ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								{t('UI_TEXT.AT_LEAST_ONE_NUMBER')}
							</p>
							<p
								style={{
									fontSize: "11px",
									color: validation.confirmpass ? "green" : "#aaa",
									marginBottom: 0,
								}}>
								{validation.confirmpass ? (
									<CheckCircleIcon fontSize="11px" />
								) : (
									<ErrorOutlineIcon fontSize="11px" />
								)}{" "}
								{t('UI_TEXT.CONFIRM_PASSWORD_MATCHES')}
							</p>
						</div>

						<div className="login-button">
							<Button
								variant="contained"
								color="primary"
								className="button-primary reset-password-button"
								type="submit"
								sx={{ minWidth: "120px" }}
								disabled={!isFormValid() || loading || tokenInfoLoading || !activeToken || !resetEmail}>
								{loading ? t('BUTTON.RESETTING_PASSWORD') : t('BUTTON.RESET_PASSWORD')}
							</Button>
						</div>
					</Form>

					{/* Verifying spinner below the button */}
					{loading && (
						<Box display="flex" alignItems="center" justifyContent="center" mt={2}>
							<CircularProgress size={24} sx={{ color: "green" }} />
							<Typography variant="body2" sx={{ marginLeft: 2, color: "green" }}>
								{t('UI_TEXT.VERIFYING')}
							</Typography>
						</Box>
					)}
				</div>

				<Link to={ROUTES.LOGIN} className="login-link">
					{t('UI_TEXT.BACK_TO_LOGIN')}
				</Link>
			</Box>
		</Box>
	);
};

export default ResetPassword;
